/**
 * A step in a `flowAsync` chain: takes the previous value of type T and returns the next one,
 * or `undefined` to short-circuit the chain.
 */
export type FlowFunction<T> = (value: T) => T | undefined | Promise<T | undefined>;

/**
 * Altered lodash _.flow to sequentially call functions of a single shared type T, threading
 * each function's result into the next call. Short-circuits (stops calling further functions)
 * only when a step returns `undefined` - any other value, including a falsy-but-defined one
 * (`0`, `''`, `false`), continues the chain.
 * https://github.com/lodash/lodash/blob/master/flow.js
 */
export function flowAsync<T>(
  ...funcs: Array<FlowFunction<T>>
): (value: T) => Promise<T | undefined> {
  const length = funcs.length;
  let index = length;
  while (index--) {
    if (typeof funcs[index] !== 'function') {
      throw new TypeError('Expected a function');
    }
  }
  return async function (value: T): Promise<T | undefined> {
    let callIndex = 0;
    let result: T | undefined = length ? await funcs[callIndex].call(null, value) : value;
    while (++callIndex < length) {
      if (result !== undefined) {
        result = await funcs[callIndex].call(null, result);
      }
    }
    return result;
  };
}
